import { CircularProgress, Stack } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAdditionalFieldsController } from 'api/controllers/AdditionalFieldsController';
import { useCounterPartyController } from 'api/controllers/CounterPartyController';

import {
  Counterparty,
  CounterpartyCreateUpdate,
  CounterpartyType,
  ExpenseType
} from 'openapi';

import { AddCounterPartyPanel } from 'components/Counterparties/AddCounterParty/AddCounterPartyPanel';

import { useTranslations } from 'context/TranslationContext';

import { AppRoutesEnum } from 'utils/routes';

export const EditCounterparty = () => {
  const { companyId, counterpartyId } = useParams();
  const navigate = useNavigate();
  const { translate } = useTranslations();
  const { getExpenseTypes } = useAdditionalFieldsController();
  const {
    getCounterpartyById,
    editCounterParty,
    getFilteredCounterpartiesByCompanyId
  } = useCounterPartyController();

  const [expenseTypeOptions, setExpenseTypeOptions] = useState<ExpenseType[]>(
    []
  );
  const [counterparty, setCounterparty] = useState<Counterparty>();

  const handleBack = useCallback(() => {
    if (!companyId) return;

    navigate(
      AppRoutesEnum.COMPANY_COUNTERPARTIES.replace(':companyId', companyId)
    );
  }, [companyId, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!companyId || !counterpartyId) return;

      try {
        const [expenseTypes, counterpartyData] = await Promise.all([
          getExpenseTypes(Number(companyId)),
          getCounterpartyById(Number(companyId), Number(counterpartyId))
        ]);

        setExpenseTypeOptions(expenseTypes);
        setCounterparty(counterpartyData);
      } catch (error) {
        console.error('Failed to load counterparty:', error);
        handleBack();
      }
    };

    fetchData();
  }, [
    companyId,
    counterpartyId,
    getExpenseTypes,
    getCounterpartyById,
    handleBack
  ]);

  const checkNameExists = useCallback(
    async (name: string, type: CounterpartyType) => {
      if (!companyId || !name.trim()) {
        return false;
      }

      const result = await getFilteredCounterpartiesByCompanyId(
        Number(companyId),
        {
          names: [name.trim().toLowerCase()],
          types: [type],
          pageNumber: 1,
          pageSize: 10
        }
      );

      return (result?.counterparties ?? []).some(
        (item: Counterparty) =>
          (item.companyCounterpartyId ?? item.id) !== Number(counterpartyId)
      );
    },
    [companyId, counterpartyId, getFilteredCounterpartiesByCompanyId]
  );

  const handleSave = async (values: CounterpartyCreateUpdate) => {
    if (!companyId || !counterpartyId) return;

    try {
      await editCounterParty(Number(companyId), Number(counterpartyId), values);
      handleBack();
    } catch (error) {
      console.error('Failed to update counterparty:', error);
    }
  };

  return companyId && counterparty ? (
    <AddCounterPartyPanel
      headerTitle={translate('labels.editCounterParty')}
      initialValues={counterparty}
      expenseTypeOptions={expenseTypeOptions}
      checkNameExists={checkNameExists}
      onClose={handleBack}
      onSubmit={handleSave}
    />
  ) : (
    <Stack alignItems="center" justifyContent="center" height="100%">
      <CircularProgress />
    </Stack>
  );
};
